import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, FileText, Trash2, Eye, Edit, Loader2, Sparkles, Image,
  Globe, BookOpen, List, LayoutGrid, Upload, CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = [
  { value: 'standard', label: 'Standard Editorial', icon: FileText, desc: 'Professional article with sections' },
  { value: 'listicle', label: 'Listicle', icon: List, desc: 'Numbered list (Top 10...)' },
  { value: 'showcase', label: 'Showcase (Bold)', icon: LayoutGrid, desc: 'Magazine-style product feature' },
];

export default function AdminBlogManager() {
  const { token } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [generatedContent, setGeneratedContent] = useState(null);

  const [form, setForm] = useState({
    topic: '', category: 'tips', blog_style: 'standard', location: 'Denver, CO',
    word_count: 1200, target_keywords: '', related_product_ids: [],
    // For save
    title: '', slug: '', meta_description: '', excerpt: '', content: '',
    featured_image: '', tags: '', mentioned_services: [], status: 'draft', is_featured: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [blogsRes, catsRes, productsRes] = await Promise.all([
        axios.get(`${API}/blogs?limit=50`, { headers }),
        axios.get(`${API}/blogs/categories/list`),
        axios.get(`${API}/services/products`)
      ]);
      setBlogs(blogsRes.data.blogs || []);
      setCategories(catsRes.data);
      setProducts(productsRes.data.products || []);
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    if (!form.topic) { toast.error('Enter a topic'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/blogs/generate`, {
        topic: form.topic,
        category: form.category,
        blog_style: form.blog_style,
        location: form.location,
        word_count: form.word_count,
        target_keywords: form.target_keywords ? form.target_keywords.split(',').map(k => k.trim()) : [],
        related_product_ids: form.related_product_ids,
      }, { headers });

      const gen = res.data.generated_content;
      setGeneratedContent(gen);
      setForm(prev => ({
        ...prev,
        title: gen.title || '',
        meta_description: gen.meta_description || '',
        excerpt: gen.excerpt || '',
        content: gen.content || '',
        tags: (gen.suggested_tags || []).join(', '),
        mentioned_services: gen.mentioned_services || [],
        slug: ''
      }));
      toast.success('Blog generated by AI!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/blogs/upload-image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, featured_image: res.data.image_url }));
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async (status = 'draft') => {
    if (!form.title || !form.content) { toast.error('Title and content required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        meta_description: form.meta_description,
        meta_keywords: form.target_keywords ? form.target_keywords.split(',').map(k => k.trim()) : [],
        excerpt: form.excerpt,
        content: form.content,
        featured_image: form.featured_image,
        category: form.category,
        blog_style: form.blog_style,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [],
        location: form.location,
        related_products: form.related_product_ids,
        mentioned_services: form.mentioned_services,
        status,
        is_featured: form.is_featured
      };

      if (editingBlog) {
        await axios.put(`${API}/blogs/${editingBlog.blog_id}`, payload, { headers });
        toast.success('Blog updated!');
      } else {
        await axios.post(`${API}/blogs`, payload, { headers });
        toast.success(`Blog ${status === 'published' ? 'published' : 'saved as draft'}!`);
      }
      setShowCreate(false);
      setEditingBlog(null);
      setGeneratedContent(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (blogId) => {
    try {
      await axios.delete(`${API}/blogs/${blogId}`, { headers });
      toast.success('Deleted');
      fetchData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const openEdit = (blog) => {
    setEditingBlog(blog);
    setForm({
      topic: blog.title, category: blog.category, blog_style: blog.blog_style || 'standard',
      location: blog.location || 'Denver, CO', word_count: 1200,
      target_keywords: (blog.meta_keywords || []).join(', '),
      related_product_ids: blog.related_products || [],
      title: blog.title, slug: blog.slug, meta_description: blog.meta_description || '',
      excerpt: blog.excerpt || '', content: blog.content || '',
      featured_image: blog.featured_image || '', tags: (blog.tags || []).join(', '),
      mentioned_services: blog.mentioned_services || [], status: blog.status,
      is_featured: blog.is_featured || false
    });
    setShowCreate(true);
  };

  const resetForm = () => {
    setForm({
      topic: '', category: 'tips', blog_style: 'standard', location: 'Denver, CO',
      word_count: 1200, target_keywords: '', related_product_ids: [],
      title: '', slug: '', meta_description: '', excerpt: '', content: '',
      featured_image: '', tags: '', mentioned_services: [], status: 'draft', is_featured: false
    });
  };

  const toggleProduct = (id) => {
    setForm(prev => ({
      ...prev,
      related_product_ids: prev.related_product_ids.includes(id)
        ? prev.related_product_ids.filter(p => p !== id)
        : [...prev.related_product_ids, id]
    }));
  };

  const styleColors = { standard: 'bg-blue-500/20 text-blue-400', listicle: 'bg-amber-500/20 text-amber-400', showcase: 'bg-purple-500/20 text-purple-400' };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-muted-foreground">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="admin-blog-manager">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground" data-testid="blog-manager-title">Blog Manager</h1>
            <p className="text-muted-foreground text-sm mt-1">{blogs.length} posts | 3 styles: Standard, Listicle, Showcase</p>
          </div>
          <Button onClick={() => { resetForm(); setEditingBlog(null); setGeneratedContent(null); setShowCreate(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-blog-btn">
            <Plus className="h-4 w-4 mr-1" /> New Blog Post
          </Button>
        </div>

        {/* Blog List */}
        <div className="space-y-2" data-testid="blog-list">
          {blogs.map(blog => (
            <Card key={blog.blog_id} className="bg-muted/60 border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {blog.featured_image ? (
                      <img src={blog.featured_image} alt="" className="w-16 h-12 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-16 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0"><FileText className="h-5 w-5 text-muted-foreground" /></div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{blog.title}</p>
                        <Badge className={`text-[9px] ${styleColors[blog.blog_style] || styleColors.standard}`}>{blog.blog_style || 'standard'}</Badge>
                        <Badge className={blog.status === 'published' ? 'bg-emerald-500/20 text-emerald-400 text-[9px]' : 'bg-slate-500/20 text-muted-foreground text-[9px]'}>{blog.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{blog.category} | {blog.views || 0} views | {new Date(blog.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}><Eye className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(blog)}><Edit className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400" onClick={() => handleDelete(blog.blog_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {blogs.length === 0 && (
            <div className="text-center py-16">
              <BookOpen className="h-14 w-14 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-foreground mb-1">No Blog Posts Yet</h3>
              <p className="text-muted-foreground text-sm">Create AI-powered blogs in 3 styles</p>
            </div>
          )}
        </div>

        {/* ===== CREATE / EDIT DIALOG ===== */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="blog-editor-dialog">
            <DialogHeader>
              <DialogTitle className="text-foreground">{editingBlog ? 'Edit Blog Post' : 'Create AI Blog Post'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Step 1: AI Generation */}
              {!editingBlog && (
                <Card className="bg-indigo-500/5 border-indigo-500/20">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="h-4 w-4 text-indigo-400" />
                      <span className="text-sm font-semibold text-indigo-400">AI Generation</span>
                    </div>

                    {/* Style Picker */}
                    <div>
                      <Label className="text-xs mb-2 block">Blog Style</Label>
                      <div className="grid grid-cols-3 gap-2" data-testid="style-selector">
                        {STYLES.map(s => (
                          <button key={s.value} onClick={() => setForm(p => ({ ...p, blog_style: s.value }))}
                            className={`p-2.5 rounded-lg border text-left transition-all ${form.blog_style === s.value ? 'bg-indigo-500/20 border-indigo-500/40' : 'bg-muted/50 border-border'}`}>
                            <s.icon className={`h-4 w-4 mb-1 ${form.blog_style === s.value ? 'text-indigo-400' : 'text-muted-foreground'}`} />
                            <p className={`text-xs font-semibold ${form.blog_style === s.value ? 'text-indigo-400' : 'text-foreground'}`}>{s.label}</p>
                            <p className="text-[10px] text-muted-foreground">{s.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Topic *</Label>
                        <Input value={form.topic} onChange={e => setForm(p => ({ ...p, topic: e.target.value }))} className="bg-muted border-border h-9" placeholder="e.g. Best Catering Options in Denver" data-testid="topic-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Category</Label>
                        <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                          <SelectTrigger className="bg-muted border-border h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{categories.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Location</Label>
                        <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} className="bg-muted border-border h-9" />
                      </div>
                      <div>
                        <Label className="text-xs">Word Count</Label>
                        <Input type="number" value={form.word_count} onChange={e => setForm(p => ({ ...p, word_count: parseInt(e.target.value) || 1200 }))} className="bg-muted border-border h-9" />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">SEO Keywords (comma-separated)</Label>
                      <Input value={form.target_keywords} onChange={e => setForm(p => ({ ...p, target_keywords: e.target.value }))} className="bg-muted border-border h-9" placeholder="denver catering, event food" />
                    </div>

                    {/* Product Picker */}
                    <div>
                      <Label className="text-xs mb-1 block">Include Products (click to toggle)</Label>
                      <div className="flex flex-wrap gap-1.5 max-h-[100px] overflow-y-auto">
                        {products.map(p => (
                          <button key={p.service_id} onClick={() => toggleProduct(p.service_id)}
                            className={`text-[10px] px-2 py-1 rounded-full border transition-all ${form.related_product_ids.includes(p.service_id) ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-400' : 'bg-muted border-border text-muted-foreground'}`}>
                            {p.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button onClick={handleGenerate} disabled={generating || !form.topic} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="generate-blog-btn">
                      {generating ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> AI is writing...</> : <><Sparkles className="h-4 w-4 mr-1" /> Generate Blog</>}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Step 2: Review & Edit */}
              {(form.title || editingBlog) && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="bg-muted border-border h-9" data-testid="title-input" /></div>
                    <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} className="bg-muted border-border h-9" placeholder="auto-generated" /></div>
                  </div>
                  <div><Label className="text-xs">Meta Description</Label><Input value={form.meta_description} onChange={e => setForm(p => ({ ...p, meta_description: e.target.value }))} className="bg-muted border-border h-9" /></div>
                  <div><Label className="text-xs">Excerpt</Label><Textarea value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} className="bg-muted border-border" rows={2} /></div>

                  {/* Featured Image */}
                  <div>
                    <Label className="text-xs mb-1 block">Featured Image</Label>
                    <div className="flex items-center gap-3">
                      {form.featured_image ? (
                        <img src={form.featured_image} alt="Featured" className="w-24 h-16 rounded-lg object-cover" />
                      ) : (
                        <div className="w-24 h-16 rounded-lg bg-muted flex items-center justify-center"><Image className="h-6 w-6 text-muted-foreground" /></div>
                      )}
                      <div className="flex gap-2">
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-muted hover:bg-accent rounded-lg border border-border text-muted-foreground transition-colors">
                            {uploadingImage ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
                          </span>
                        </label>
                        <Input value={form.featured_image} onChange={e => setForm(p => ({ ...p, featured_image: e.target.value }))} className="bg-muted border-border h-8 text-xs flex-1" placeholder="Or paste URL..." />
                      </div>
                    </div>
                  </div>

                  {/* Content - HTML editor */}
                  <div>
                    <Label className="text-xs">Content (HTML)</Label>
                    <Textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} className="bg-muted border-border font-mono text-xs" rows={12} data-testid="content-editor" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} className="bg-muted border-border h-9" /></div>
                    <div className="flex items-end gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.is_featured} onChange={e => setForm(p => ({ ...p, is_featured: e.target.checked }))} className="rounded bg-muted border-border" />
                        <span className="text-xs text-muted-foreground">Featured Post</span>
                      </label>
                    </div>
                  </div>

                  {/* Mentioned Services Preview */}
                  {form.mentioned_services.length > 0 && (
                    <div>
                      <Label className="text-xs mb-1 block">Mentioned Services (sidebar)</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {form.mentioned_services.map((s, i) => (
                          <Badge key={i} className="bg-indigo-500/20 text-indigo-400 text-[10px]">{s.name || s.service_id}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Save Actions */}
                  <div className="flex items-center justify-between pt-2">
                    <Button variant="outline" onClick={() => handleSave('draft')} disabled={saving} data-testid="save-draft-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />} Save Draft
                    </Button>
                    <Button onClick={() => handleSave('published')} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="publish-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Globe className="h-4 w-4 mr-1" />} Publish
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
